<script lang="ts">
  import { onMount } from "svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import { bottleTopsEnglish } from "@shared/filters/bottle_tops_english";
  import { capitalize } from "lodash";
  import { convertToLocal } from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";
  import { soldInCases, soldInUnits, isKeg } from "@shared/svelte/ProductHelpers.js";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import { productContainerDetailsShort } from "@shared/filters/product_container_details";
  import { sizeAndUnitsDisplay } from "@shared/services/product";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import CartRecommendationsCartWidgetContainer from "@retailer/svelte/CartWidget/v2/Containers/CartRecommendationsCartWidgetContainer.svelte";
  import moneyFormatters from "@shared/filters/money.js";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import Skeleton from "svelte-skeleton/Skeleton.svelte";

  import { CartRecommendationProductEntity } from "@shared/services/event_tracking/entities";
  import type { CartOrderType } from "@shared/types/Cart";
  import type { Product } from "@shared/types/ProductLine";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";

  const { trackMrcEvents } = EventTracking;

  export let product;
  export let products: Product[] = [];
  export let order: CartOrderType;
  export let futurePrices = [];
  export let futurePricesLoading: boolean = true;
  export let usesFuturePricingWithMinBottle: boolean;

  const formatMoney = price => {
    return moneyFormatters.formatWithDecimal(price);
  };

  let inventoryItemLoaded: boolean = false;

  onMount(async () => {
    inventoryItemLoaded = false;
    const response = await InventoryItemStore.fetchInventoryItem(product.inventory_id);
    inventoryItemLoaded = response?.loaded;
  });

  $: inventoryItemSoldBy = $InventoryItemStore?.inventoryItems[product.inventory_id]?.sold_by;
  const isSoldInCases = soldInCases(product, product.product_line, inventoryItemSoldBy);
  const isSoldInUnits =
    soldInUnits(product, product.product_line, inventoryItemSoldBy) || isKeg(product);
  const caseInfo = productContainerDetailsShort(product);

  let containerCaseSize = product.container_case_size;

  let pricingForProduct, caseLevelPrice, casePriceForDisplay, oneBottlePrice;
  let showMinBottle = false;
  $: {
    if (usesFuturePricingWithMinBottle) {
      pricingForProduct = futurePrices.find(p => p.inventory_id === product.inventory_id);
      if (pricingForProduct) {
        oneBottlePrice = pricingForProduct.current_prices.find(p => p.bottles == 1);
        if (containerCaseSize && containerCaseSize > 1) {
          caseLevelPrice = pricingForProduct.current_prices.find(
            p => p.bottles == containerCaseSize,
          );
        }
      }
      casePriceForDisplay = caseLevelPrice || oneBottlePrice;
      showMinBottle = Boolean(oneBottlePrice && caseLevelPrice);
    }
  }

  const formatPrice = () => {
    let price = "";
    if (isSoldInUnits) {
      price += `${formatMoney(product.unit_price)}/${product.container_type}`;
    }
    if (isSoldInUnits && isSoldInCases) {
      price += " - ";
    }
    if (isSoldInCases) {
      price += `${formatMoney(product.case_price)}/case`;
    }
    return price;
  };

  const formatPostOffPrice = () => {
    let price = "";
    price += `${formatMoney(oneBottlePrice.price_per_unit)} `;
    if (showMinBottle) {
      price += "min bottle";
    } else {
      price += product.container_type;
    }

    if (isSoldInCases) {
      price += ` - ${formatMoney(casePriceForDisplay.price_per_case)}`;
      if (casePriceForDisplay.discount_per_case > 0) {
        price += " ";
        price += `<span class="strikethrough">${formatMoney(casePriceForDisplay.list)}</span>`;
      }
      price += "/case";
    }

    return price;
  };

  const formatLastOrderedInfo = () => {
    let lastOrderedInfo = "";
    const lastOrderedUnits = product.last_ordered_info.last_order_unit_quantity;
    const lastOrderedCases = product.last_ordered_info.last_order_case_quantity;
    if (lastOrderedUnits) {
      lastOrderedInfo += `${pluralize(
        product.container_type,
        product.last_ordered_info.last_order_unit_quantity,
        true,
      )}`;
    }
    if (lastOrderedCases > 0) {
      if (lastOrderedUnits) {
        lastOrderedInfo += ", ";
      }
      lastOrderedInfo += `${pluralize(
        "case",
        product.last_ordered_info.last_order_case_quantity,
        true,
      )} `;
    }

    return lastOrderedInfo;
  };

  const trackingParams = [
    new CartRecommendationProductEntity({
      product_id: product.id,
      inventory_id: product.inventory.id,
      order_id: order.id,
      distributor_id: order.distributor.id,
    }),
  ];

  let cartProductWidth: number = 0;
</script>

<style>
  .cart-recommendations-container {
    align-items: start;
  }

  .last-order-desktop-container {
    min-width: 250px;
  }
</style>

<div
  class="fizz-inset-square-24 cart__item"
  data-testid={`recommendations-product-${product.id}`}
  bind:clientWidth={cartProductWidth}
  use:trackMrcEvents={[trackingParams]}
>
  <div class="fizz-flex-row cart-recommendations-container">
    <ProductImage
      product={product.product_line}
      width={72}
      height={72}
      fizz={true}
      className="cart-recommendations-product-image"
      usePlaceholderImage={true}
      extraAttrs={{ id: "product-card-image" }}
    />
    <div class="fizz-full-width">
      <div class="fizz-flex-row-8 fizz-align-content-start fizz-flex-item fizz-space-between">
        <div class="fizz-flow-4">
          <p class="fizz-text-strong">
            {product.product_line.name}
          </p>
          <p class="fizz-text-small">
            {sizeAndUnitsDisplay(product)}
            {#if product.container_type == "bottle" && product.material}
              {capitalize(product.material)}
            {/if}
            {#if product.container_type == "bottle"}
              {capitalize(bottleTopsEnglish(product.bottle_tops))}
            {/if}
            {capitalize(product.container_type)}
            {#if product.vintage_year}
              ({product.vintage_year})
            {/if}
            {#if caseInfo}
              - {caseInfo}
            {/if}
            {#if product.inventory.sku}
              (SKU: {product.inventory.sku})
            {/if}
          </p>
          {#if usesFuturePricingWithMinBottle && oneBottlePrice}
            {#if !futurePricesLoading}
              <p class="fizz-text-subdued fizz-stack-8 fizz-text-small">
                {@html formatPostOffPrice()}
              </p>
            {:else}
              <div class="fizz-stack-8">
                <Skeleton animate={true} height={10} width={300}>
                  <rect width="175" height="8" x="0" y="0" rx="8" ry="8" />
                </Skeleton>
              </div>
            {/if}
          {:else if (product.case_price || product.unit_price) && !usesFuturePricingWithMinBottle}
            <p class="fizz-text-subdued fizz-stack-8 fizz-text-small">
              {formatPrice()}
            </p>
          {:else}
            <div class="fizz-tooltip-container fizz-stack-8">
              <button class="fizz-tooltip-trigger fizz-flex-row-8">
                <span class="fizz-underline-dashed fizz-text-subdued" style="font-weight: 400;">
                  Price not available
                </span>
              </button>
              <div
                class="fizz-tooltip fizz-tooltip-left fizz-tooltip-top"
                class:fizz-tooltip-top={$LayoutStore.isDesktop}
                role="tooltip"
                style="--max-width: 260px;"
              >
                This distributor does not provide us with pricing information.
              </div>
            </div>
          {/if}
        </div>
        {#if cartProductWidth > 700}
          <div
            class="fizz-text-subdued fizz-text-small fizz-flex-row-4 last-order-desktop-container"
          >
            <div class="fizz-align-self-start" style="padding-top: .05rem;">
              <Icon fill="#595959" width={16} height={16} type="History" />
            </div>
            <p>
              <strong>
                Last ordered on {convertToLocal(
                  product.last_ordered_info.last_order_submitted_at,
                )}:&nbsp;
              </strong>
              {formatLastOrderedInfo()}
            </p>
          </div>
        {/if}
      </div>

      {#if cartProductWidth < 700}
        <div class="fizz-text-subdued fizz-text-small fizz-flex-row-4 fizz-stack-8">
          <div class="fizz-align-self-start" style="padding-top: .05rem;">
            <Icon fill="#595959" width={16} height={16} type="History" />
          </div>
          <span>
            <strong>
              Last ordered on {convertToLocal(
                product.last_ordered_info.last_order_submitted_at,
              )}:&nbsp;
            </strong>
            {formatLastOrderedInfo()}
          </span>
        </div>
      {/if}

      <CartRecommendationsCartWidgetContainer
        inventoryId={product.inventory_id}
        {product}
        {products}
        distributor={order.distributor}
        trackingEntities={trackingParams}
        loaded={inventoryItemLoaded}
      />
    </div>
  </div>
</div>
